import { bookingCalendarApi } from "@/app/booking-calendar/bookingCalendarApi"

// ── Types ────────────────────────────────────────────────────────────
// Mirror AmsBookingSearchRequest (AmsBookingRequestsController.cs) and AmsBookingSearchPage /
// AmsBookingSearchResult (AmsBookingSearchModels.cs). Statuses travel as the numbers the server
// stores — see lib/bookingStatus.ts.

/** The body of `POST api/ams/booking-requests/search`. */
export interface BookingSearchRequest {
  /** "YYYY-MM-DD", the first Pacific date searched, inclusive. */
  from: string
  /** "YYYY-MM-DD", the last Pacific date searched, inclusive. */
  to: string
  /** Part of the customer's first or last name. */
  customerName?: string
  /**
   * Part of the address, e.g. "Tobin St" or a ZIP code; matched word by word, ignoring case,
   * punctuation, unit markers and the common abbreviations.
   */
  address?: string
  /** A phone number in any format; the server reduces it to ten digits. */
  phoneNumber?: string
  /** An email address, matched whole. */
  email?: string
  /** A booking reference code, matched whole. */
  referenceCode?: string
  /** Any of these statuses (AmsBookingRequestStatus). */
  statuses?: number[]
  /** An energy account number, matched whole ignoring spaces, dashes and case. */
  energyAccountNumber?: string
  /** A six-digit confirmation code, matched exactly; several bookings may share one. */
  confirmationCode?: string
  skip?: number
  take?: number
}

/**
 * The same body with a Social Security number: nine digits, for a caller holding
 * `appointments:reveal-ssn`. Its own type, sent only through the `searchBookingRequestsBySsn`
 * mutation — never the query, whose arguments become its cache key in the store.
 */
export interface BookingSsnSearchRequest extends BookingSearchRequest {
  ssn: string
}

/** One appointment the search found. */
export interface BookingSearchResult {
  requestId: string
  referenceCode: string
  customerName: string | null
  phoneNumber: string | null
  email: string | null
  /** The appointment's start, or null for a request that never reached a slot. */
  start: string | null
  siteName: string | null
  calTeamSlug: string
  hostCalUserId: number | null
  hostName: string | null
  status: number
  /** The Cal booking, once one exists. A row without one has nothing to open. */
  calBookingUid: string | null
}

export interface BookingSearchPage {
  items: BookingSearchResult[]
  /** How many appointments match across every page. */
  totalCount: number
  skip: number
  take: number
  hasMore: boolean
}

/** What the server said was wrong, when it said: a 400's problem detail, else a word for the status. */
export function searchErrorMessage(error: unknown): string {
  const failure = error as { status?: unknown; data?: { detail?: unknown } } | undefined
  if (failure?.status === 400 && typeof failure.data?.detail === "string") return failure.data.detail
  if (failure?.status === 403) return "You do not have permission to search appointments."
  return "The search could not be completed."
}

// A POST so the criteria — names, phone numbers, email addresses — travel in the body rather than the
// URL, where they would land in access logs and browser history.
//
// A query rather than a mutation so that it carries tags: cancelling, reassigning or recording an
// outcome in the appointment dialog invalidates AmsBookings / AmsBookingStatus, and the results are
// fetched again to show it. Dropped from the store the moment the page stops showing it
// (`keepUnusedDataFor: 0`), so what was searched for does not linger in memory after the page closes;
// nothing here is ever written to storage.
//
// A search by Social Security number (#771) never goes through that query: a query's arguments are
// its cache key, held in the store for as long as the entry lives. It goes through the mutation
// below, dispatched directly with `bookingSearchApi.endpoints.searchBookingRequestsBySsn.initiate`;
// the caller takes the result and resets the mutation at once (see `runSsnSearch`), which drops the
// arguments it recorded in the store. It provides no tags, so nothing refetches it: the number is not
// kept to refetch with.
//
// Its generated hook (`useSearchBookingRequestsBySsnMutation`) is deliberately NOT exported: the hook
// keeps the promise of its last call in the calling component's React state and exposes its
// `originalArgs` — the whole request, number included — until the next call or unmount, and resetting
// the mutation in the store does not clear it.
export const bookingSearchApi = bookingCalendarApi.injectEndpoints({
  endpoints: (builder) => ({
    searchBookingRequests: builder.query<BookingSearchPage, BookingSearchRequest>({
      query: (body) => ({
        url: "/api/ams/booking-requests/search",
        method: "POST",
        body,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["AmsBookingStatus", "AmsBookings"],
    }),
    searchBookingRequestsBySsn: builder.mutation<BookingSearchPage, BookingSsnSearchRequest>({
      query: (body) => ({
        url: "/api/ams/booking-requests/search",
        method: "POST",
        body,
      }),
    }),
  }),
})

// No mutation hook for the search by number: see above.
export const { useSearchBookingRequestsQuery } = bookingSearchApi
